/**
 * The signed-in staff member, for the approval screens.
 *
 * Kept in sessionStorage rather than localStorage: closing the tab ends it, which suits a shared
 * office machine better than a token that sits there until someone clears it. The backend expires
 * the session anyway, so this is about the browser, not the security.
 */

const TOKEN_KEY = 'AdminToken';
const NAME_KEY = 'AdminName';
const METHOD_KEY = 'AdminSignInMethod';
const CAMPUSES_KEY = 'AdminCampuses';
const LEVEL_KEY = 'AdminAccessLevel';
const MANAGE_KEY = 'AdminCanManageAccess';
const TEAM_KEY = 'AdminTeam';
// "View as", for an access manager: the team whose screens they are looking at, if not their own.
const VIEW_AS_KEY = 'AdminViewAs';

const store = () => {
    try {
        return window.sessionStorage;
    } catch {
        // Private browsing, or storage blocked. Signing in still works for this page view.
        return null;
    }
};

export const saveAdminSession = ({
    token, name, signInMethod, campuses, allCampuses, accessLevel, canManageAccess, team,
}) => {
    const storage = store();
    if (!storage || !token) return;

    storage.setItem(TOKEN_KEY, token);
    if (name) storage.setItem(NAME_KEY, name);
    if (signInMethod) storage.setItem(METHOD_KEY, signInMethod);
    // Only so the screen can say whose re-enrolments these are. The server decides what may
    // actually be seen, and checks it on every request.
    storage.setItem(CAMPUSES_KEY, JSON.stringify(allCampuses ? 'all' : (campuses || [])));

    // Whether they may approve, and whether they may hand out access. Both are decided again on
    // the server for every request — these only keep the screen from offering what would be
    // refused, which is kinder than a button that always fails.
    if (accessLevel) storage.setItem(LEVEL_KEY, accessLevel);
    if (canManageAccess !== undefined) storage.setItem(MANAGE_KEY, canManageAccess ? '1' : '0');

    // Which half of a re-enrolment they work on. This one decides what the screens draw rather
    // than what may be pressed — and the endpoints leave the other team's fields out of the
    // response entirely, so a changed value here reveals nothing.
    if (team) storage.setItem(TEAM_KEY, team);
};

/** The campuses this staff member covers: an array, or 'all' for the primary owner. */
export const getAdminCampuses = () => {
    try {
        const stored = store()?.getItem(CAMPUSES_KEY);
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        return parsed === 'all' ? 'all' : (Array.isArray(parsed) ? parsed : []);
    } catch {
        return [];
    }
};

/** "Kewdale and Thornlie re-enrolments", or "All campuses" — for the heading. */
export const campusScopeLabel = () => {
    const campuses = getAdminCampuses();
    if (campuses === 'all') return 'All campuses';
    if (campuses.length === 0) return 'No campus assigned';
    if (campuses.length === 1) return campuses[0];
    return `${campuses.slice(0, -1).join(', ')} and ${campuses[campuses.length - 1]}`;
};

export const getAdminToken = () => store()?.getItem(TOKEN_KEY) || null;
export const getAdminName = () => store()?.getItem(NAME_KEY) || '';
export const getAdminSignInMethod = () => store()?.getItem(METHOD_KEY) || '';

export const clearAdminSession = () => {
    // A view chosen by one person must not carry over to whoever signs in next.
    const storage = store();
    if (!storage) return;

    [TOKEN_KEY, NAME_KEY, METHOD_KEY, CAMPUSES_KEY, LEVEL_KEY, MANAGE_KEY, TEAM_KEY, VIEW_AS_KEY]
        .forEach((key) => storage.removeItem(key));
};

export const isAdminSignedIn = () => Boolean(getAdminToken());

/**
 * 'view' or 'approve'.
 *
 * Unknown is treated as 'approve': a Microsoft sign-in has not asked the server yet, and someone
 * whose access came from the environment lists has always been able to approve. The server
 * refuses either way if it disagrees.
 */
export const getAdminAccessLevel = () => store()?.getItem(LEVEL_KEY) || 'approve';

/** Whether to offer the Approve button at all. */
export const mayApprove = () => getAdminAccessLevel() !== 'view' && seesFamilyDetail();

/**
 * The team this staff member is on: 'enrolment', 'accounts', or 'both'.
 *
 * The enrolment team reads what a family changed and decides about it; the accounts team reads
 * the Health Care Card and the money. Unknown is 'both', which is what somebody signed in
 * before the teams existed has always seen — and what the server sends them.
 */
export const getAdminTeam = () => store()?.getItem(TEAM_KEY) || 'both';

/** The money: what was charged, paid, owed, and taken from credit. */
export const seesAmounts = () => getAdminTeam() !== 'enrolment';

/** What the family and their children changed, and the approval that follows from it. */
export const seesFamilyDetail = () => getAdminTeam() !== 'accounts';

/** The Health Care Card, which is only ever a question about a discount. */
export const seesHealthCareCard = () => getAdminTeam() !== 'enrolment';

/** Whether to show the screen that hands out access. */
export const canManageAccess = () => store()?.getItem(MANAGE_KEY) === '1';

/** Whether Microsoft sign-in has been set up for this build. */
export const isMicrosoftSignInConfigured = () =>
    Boolean(process.env.REACT_APP_AZURE_CLIENT_ID && process.env.REACT_APP_AZURE_TENANT_ID);

/**
 * The password sign-in is a stopgap for dev and uat. The backend refuses it in production
 * regardless, so this only decides whether to offer it on screen.
 */
export const isPasswordSignInOffered = () =>
    String(process.env.REACT_APP_ALLOW_ADMIN_PASSWORD_LOGIN).toLowerCase() === 'true';

/**
 * "View as": an access manager looking at the staff screens as one team does. Sent with every staff
 * request (api/clients.js); the server honours it only for an access manager and then answers as for
 * that team, so what is drawn is what that team is actually sent. '' is their own view.
 */
export const VIEW_AS_TEAMS = ['enrolment', 'accounts', 'both'];

export const getAdminViewAs = () => {
    const value = store()?.getItem(VIEW_AS_KEY) || '';
    return VIEW_AS_TEAMS.includes(value) ? value : '';
};

export const setAdminViewAs = (team) => {
    const storage = store();
    if (!storage) return;
    if (VIEW_AS_TEAMS.includes(team)) storage.setItem(VIEW_AS_KEY, team);
    else storage.removeItem(VIEW_AS_KEY);
};
